import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Buyers.css';
import { SortArrows } from '../assets/icons';
import { colour, formatterLong, numberFormatter } from '../styles/GlobalStyles';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, MeasuringStrategy } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge, Input } from './';
import { updateBuyer, findDuplicateBuyer } from '../functions';
import { HugeiconsIcon } from '@hugeicons/react';
import { Delete02Icon, SentIcon } from '@hugeicons/core-free-icons';

// Editable fields configuration
const EDITABLE_FIELDS = {
    name:         { path: 'contact.name', type: 'text' },
    email:        { path: 'contact.email', type: 'text' },
    phone:        { path: 'contact.phone', type: 'text' },
    priceMin:     { path: 'priceRange.min', type: 'currency', min: 0 },
    priceMax:     { path: 'priceRange.max', type: 'currency', min: 0 },
    minProfit:    { path: 'profitThresholds.minProfitDollars', type: 'currency', min: 0 },
    minProfitPct: { path: 'profitThresholds.minProfitPercentage', type: 'number', min: 0, max: 100 },
    maxArvPct:    { path: 'profitThresholds.maxPurchasePricePercentOfARV', type: 'number', min: 0, max: 100 },
    minRoi:       { path: 'profitThresholds.minRoiPercent', type: 'number', min: 0 },
    source:       { path: 'source', type: 'text' },
};

// Standard column definitions
const BUYER_COLUMNS = [
    { key: 'active',        label: 'Status',         width: '80px',   sortable: false },
    { key: 'name',          label: 'Name',           width: '160px',  sortable: true },
    { key: 'email',         label: 'Email',          width: '180px',  sortable: false },
    { key: 'phone',         label: 'Phone',          width: '140px',  sortable: false },
    { key: 'source',        label: 'Source',         width: '120px',  sortable: true },
    { key: 'geography',     label: 'Geography',      width: '200px',  sortable: false },
    { key: 'priceMin',      label: 'Price Min',      width: '110px',  sortable: false },
    { key: 'priceMax',      label: 'Price Max',      width: '110px',  sortable: false },
    { key: 'minProfit',     label: 'Min Profit',     width: '110px',  sortable: false },
    { key: 'minProfitPct',  label: 'Min Profit %',   width: '100px',  sortable: false },
    { key: 'maxArvPct',     label: 'Max % ARV',      width: '100px',  sortable: false },
    { key: 'minRoi',        label: 'Min ROI %',      width: '100px',  sortable: false },
    { key: 'rehabLevels',   label: 'Rehab Levels',   width: '160px',  sortable: false },
    { key: 'propertyTypes', label: 'Property Types',  width: '160px', sortable: false },
    { key: 'propReqs',      label: 'Property Specs',  width: '180px', sortable: false },
    { key: 'pool',          label: 'Pool',            width: '80px',  sortable: false },
    { key: 'garage',        label: 'Garage',          width: '100px', sortable: false },
    { key: 'roles',         label: 'Roles',            width: '140px', sortable: false },
    { key: 'dealStructure', label: 'Deal Structure',  width: '140px', sortable: false },
    { key: 'createdAt',     label: 'Created',        width: '120px',  sortable: true },
    { key: 'updatedAt',     label: 'Updated',        width: '120px',  sortable: true },
];

// Property type short labels
const PROP_TYPE_SHORT = {
    single_family: 'SFH', multi_family: 'Multi-Family', townhouse: 'Townhouse',
    condo: 'Condo', mobile: 'Mobile', commercial: 'Commercial', land: 'Land', portfolio: 'Portfolio'
};

// Draggable header cell component
function DraggableHeader({ column, sortColumn, sortDirection, hoveredColumn, onSort, onHoverColumn }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: column.key });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : 'auto',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`buyers-table-cell buyers-table-header-cell body-regular colour-secondary ${isDragging ? 'buyers-header-dragging' : ''}`}
            onClick={() => column.sortable && onSort(column.key)}
            onMouseEnter={() => column.sortable && onHoverColumn(column.key)}
            onMouseLeave={() => onHoverColumn(null)}
            {...attributes}
            {...listeners}
        >
            <span>{column.label}</span>
            {column.sortable && (
                <SortArrows
                    fill={sortColumn === column.key ? colour.gray06 : (hoveredColumn === column.key ? colour.gray04 : colour.gray00)}
                    className={sortColumn === column.key ? '' : 'opacity-50'}
                />
            )}
        </div>
    );
}

function BuyersTable({
    data,
    allBuyers = [],
    loading = false,
    sortColumn,
    sortDirection,
    hoveredColumn,
    onSort,
    onHoverColumn,
    visibleColumns = null,
    columnOrder = null,
    onColumnReorder = null,
    companyId = null,
    onBuyerUpdate = null,
    onToggleActive = null,
    onOpenGeoModal = null,
    onDeleteBuyer = null,
    onSendDeals = null
}) {
    const navigate = useNavigate();
    const [hoveredRow, setHoveredRow] = useState(null);

    // Inline editing state
    const [editingCell, setEditingCell] = useState(null);
    const [editedValues, setEditedValues] = useState({});
    const [savingCell, setSavingCell] = useState(null);
    const currentInputValuesRef = useRef({});
    const baselineValuesRef = useRef({});
    const blurTimeoutRef = useRef(null);

    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
                blurTimeoutRef.current = null;
            }
        };
    }, []);

    // ==================== Inline Editing Functions ====================

    const getCellKey = (buyerId, field) => `${buyerId}-${field}`;

    const isEditing = (buyerId, field) => {
        return editingCell && editingCell.buyerId === buyerId && editingCell.field === field;
    };

    const isSaving = (buyerId, field) => {
        return savingCell && savingCell.buyerId === buyerId && savingCell.field === field;
    };

    const parseValue = (value, field) => {
        const config = EDITABLE_FIELDS[field];
        if (!config) return null;

        if (config.type === 'text') {
            return value.toString().trim();
        }

        const cleaned = value.toString().replace(/[$,%]/g, '').trim();
        if (cleaned === '') return null;

        if (config.type === 'currency') {
            const parsed = parseFloat(cleaned.replace(/,/g, ''));
            return isNaN(parsed) ? null : parsed;
        }
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? null : parsed;
    };

    const isValidInput = (value, field) => {
        if (value === '' || value === null || value === undefined) return true;
        const config = EDITABLE_FIELDS[field];
        if (!config) return false;

        if (config.type === 'text') return true;

        const cleaned = value.toString().replace(/[$,%]/g, '').replace(/,/g, '').trim();
        if (cleaned === '') return true;

        const numberRegex = /^-?\d*\.?\d*$/;
        if (!numberRegex.test(cleaned)) return false;

        const parsed = parseFloat(cleaned);
        if (isNaN(parsed)) return false;
        if (config.min !== undefined && parsed < config.min) return false;
        if (config.max !== undefined && parsed > config.max) return false;

        return true;
    };

    const getRawValue = (item, field) => {
        switch (field) {
            case 'name': return item.contact?.name;
            case 'email': return item.contact?.email;
            case 'phone': return item.contact?.phone;
            case 'priceMin': return item.priceRange?.min;
            case 'priceMax': return item.priceRange?.max;
            case 'minProfit': return item.profitThresholds?.minProfitDollars;
            case 'minProfitPct': return item.profitThresholds?.minProfitPercentage;
            case 'maxArvPct': return item.profitThresholds?.maxPurchasePricePercentOfARV;
            case 'minRoi': return item.profitThresholds?.minRoiPercent;
            case 'source': return item.source;
            default: return null;
        }
    };

    const getDisplayValue = (buyerId, field, originalValue) => {
        const key = getCellKey(buyerId, field);
        const edited = editedValues[key];
        if (edited !== undefined && edited !== null) {
            return edited;
        }
        return originalValue !== null && originalValue !== undefined ? originalValue.toString() : '';
    };

    const handleCellClick = (e, buyerId, field, originalValue) => {
        e.stopPropagation();
        if (!companyId) return;

        setEditingCell({ buyerId, field });
        const key = getCellKey(buyerId, field);

        let baselineValue = originalValue !== null && originalValue !== undefined
            ? originalValue.toString()
            : '';

        const config = EDITABLE_FIELDS[field];
        if (config?.type === 'currency' && baselineValue !== '') {
            const numValue = parseFloat(baselineValue.replace(/,/g, ''));
            if (!isNaN(numValue)) {
                baselineValue = numberFormatter.format(numValue);
            }
        }

        baselineValuesRef.current[key] = baselineValue;
        currentInputValuesRef.current[key] = baselineValue;
        setEditedValues(prev => ({ ...prev, [key]: baselineValue }));
    };

    const handleCellChange = (buyerId, field, value) => {
        const key = getCellKey(buyerId, field);
        const config = EDITABLE_FIELDS[field];
        let formattedValue = value;

        if (config?.type === 'currency') {
            const cleaned = value.replace(/,/g, '');
            if (cleaned !== '' && /^\d+$/.test(cleaned)) {
                formattedValue = numberFormatter.format(parseInt(cleaned, 10));
            } else {
                formattedValue = cleaned;
            }
        }

        currentInputValuesRef.current[key] = formattedValue;
        setEditedValues(prev => ({ ...prev, [key]: formattedValue }));
    };

    const handleCellBlur = (buyerId, field, rawValue) => {
        blurTimeoutRef.current = setTimeout(() => {
            handleCellSave(buyerId, field, rawValue);
            blurTimeoutRef.current = null;
        }, 100);
    };

    const buildNestedUpdate = (path, value) => {
        const keys = path.split('.');
        let obj = {};
        let current = obj;
        for (let i = 0; i < keys.length - 1; i++) {
            current[keys[i]] = {};
            current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return obj;
    };

    const handleCellSave = async (buyerId, field, originalValue) => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }

        const key = getCellKey(buyerId, field);
        const editedValue = currentInputValuesRef.current[key] ?? editedValues[key];

        if (editedValue === undefined || editedValue === null) {
            setEditingCell(null);
            return;
        }

        const parsed = parseValue(editedValue, field);
        const config = EDITABLE_FIELDS[field];

        if (config.type !== 'text' && parsed === null && editedValue !== '') {
            const baselineValue = baselineValuesRef.current[key];
            if (baselineValue !== undefined) {
                setEditedValues(prev => ({ ...prev, [key]: baselineValue }));
            }
            setEditingCell(null);
            return;
        }

        const newValue = config.type === 'text' ? (parsed || '') : parsed;
        const originalParsed = originalValue !== null && originalValue !== undefined ? originalValue : null;

        if (newValue === originalParsed || (newValue === null && originalParsed === null) || (config.type === 'text' && newValue === (originalParsed || ''))) {
            setEditingCell(null);
            setEditedValues(prev => {
                const updated = { ...prev };
                delete updated[key];
                return updated;
            });
            delete baselineValuesRef.current[key];
            delete currentInputValuesRef.current[key];
            return;
        }

        setEditedValues(prev => ({ ...prev, [key]: newValue !== null ? newValue.toString() : '' }));
        currentInputValuesRef.current[key] = newValue !== null ? newValue.toString() : '';
        setEditingCell(null);

        // Check for duplicate email/phone before saving
        if ((field === 'email' || field === 'phone') && newValue) {
            const checkContact = field === 'email' ? { email: newValue } : { phone: newValue };
            const otherBuyers = allBuyers.filter(b => (b.id || b.buyerId) !== buyerId);
            const dupResult = findDuplicateBuyer(otherBuyers, checkContact);
            if (dupResult.isDuplicate) {
                const fieldLabel = field === 'email' ? 'email address' : 'phone number';
                console.error(`Duplicate ${fieldLabel}: matches buyer "${dupResult.matchedBuyerName}"`);
                const baselineValue = baselineValuesRef.current[key];
                if (baselineValue !== undefined) {
                    currentInputValuesRef.current[key] = baselineValue;
                    setEditedValues(prev => ({ ...prev, [key]: baselineValue }));
                } else {
                    setEditedValues(prev => {
                        const updated = { ...prev };
                        delete updated[key];
                        return updated;
                    });
                }
                return;
            }
        }

        setSavingCell({ buyerId, field });

        try {
            const fieldConfig = EDITABLE_FIELDS[field];
            const updateData = buildNestedUpdate(fieldConfig.path, newValue);
            const result = await updateBuyer(companyId, buyerId, updateData);
            if (result.status !== 200) {
                throw new Error(result.message || 'Failed to save buyer field');
            }

            if (onBuyerUpdate) {
                onBuyerUpdate(buyerId, field, newValue);
            }

            delete baselineValuesRef.current[key];
            setSavingCell(null);
        } catch (error) {
            console.error('Error saving buyer field:', error);
            const baselineValue = baselineValuesRef.current[key];
            if (baselineValue !== undefined) {
                currentInputValuesRef.current[key] = baselineValue;
                setEditedValues(prev => ({ ...prev, [key]: baselineValue }));
            } else {
                setEditedValues(prev => {
                    const updated = { ...prev };
                    delete updated[key];
                    return updated;
                });
            }
            setSavingCell(null);
        }
    };

    const renderEditableCell = (item, field, displayValue, rawValue) => {
        const buyerId = item.id || item.buyerId;
        const isEditingThis = isEditing(buyerId, field);
        const isSavingThis = isSaving(buyerId, field);
        const key = getCellKey(buyerId, field);
        const config = EDITABLE_FIELDS[field];

        if (isEditingThis) {
            const currentValue = editedValues[key] ?? (rawValue !== null && rawValue !== undefined ? rawValue.toString() : '');

            return (
                <div className="buyers-edit-container" onClick={(e) => e.stopPropagation()}>
                    <Input
                        key={`input-${key}`}
                        label={null}
                        labelType={null}
                        value={currentValue}
                        onChange={(e) => handleCellChange(buyerId, field, e.target.value)}
                        onBlur={() => handleCellBlur(buyerId, field, rawValue)}
                        placeholder=""
                        type="text"
                        leftUnit={config.type === 'currency' ? '$' : null}
                        rightUnit={null}
                        unitSwitch={false}
                        leftUnitSelected={true}
                        unitSwitchFunc={null}
                        disabled={isSavingThis}
                        multiline={false}
                        error={!isValidInput(currentValue, field)}
                        onMouseEnter={null}
                        onMouseLeave={null}
                        reset={false}
                        resetFunc={null}
                        resetPosition={null}
                        focusMultiline={true}
                        prompt={false}
                        submitPromptDisabled={false}
                        submitPrompt={null}
                        onHeightChange={null}
                    />
                </div>
            );
        }

        const displayVal = getDisplayValue(buyerId, field, rawValue);

        let formattedValue;
        if (config.type === 'currency') {
            formattedValue = formatCurrency(parseValue(displayVal, field));
        } else if (config.type === 'number') {
            const numVal = parseValue(displayVal, field);
            formattedValue = numVal !== null && !isNaN(numVal) ? `${numVal}%` : '-';
        } else {
            formattedValue = displayVal || '-';
        }

        return (
            <span
                className={`buyers-editable-cell ${companyId ? 'buyers-editable-cell-clickable' : ''}`}
                onClick={(e) => companyId && handleCellClick(e, buyerId, field, rawValue)}
                title={companyId ? 'Click to edit' : undefined}
            >
                {formattedValue || '-'}
            </span>
        );
    };

    // ==================== End Inline Editing Functions ====================

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        }),
        useSensor(KeyboardSensor)
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id && onColumnReorder) {
            onColumnReorder(active.id, over.id);
        }
    };

    // Formatting functions
    const formatCurrency = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        return formatterLong.format(value).replace('.00', '');
    };

    const formatDateTime = (date) => {
        if (!date) return '-';
        let dateObj;
        if (date && typeof date.toDate === 'function') {
            dateObj = date.toDate();
        } else if (date && typeof date === 'object' && date._seconds !== undefined) {
            const milliseconds = date._seconds * 1000 + (date._nanoseconds || 0) / 1000000;
            dateObj = new Date(milliseconds);
        } else if (date instanceof Date) {
            dateObj = date;
        } else if (typeof date === 'number') {
            dateObj = new Date(date);
        } else if (typeof date === 'string') {
            dateObj = new Date(date);
        } else {
            return '-';
        }
        if (isNaN(dateObj.getTime())) return '-';

        const now = new Date();
        const diffMs = now - dateObj;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const isToday = dateObj.getDate() === now.getDate() &&
                    dateObj.getMonth() === now.getMonth() &&
                    dateObj.getFullYear() === now.getFullYear();
        const diffMonths = (now.getFullYear() - dateObj.getFullYear()) * 12 +
                        (now.getMonth() - dateObj.getMonth());

        if (isToday && diffMinutes < 60) {
            return diffMinutes <= 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
        }
        if (diffHours < 24) {
            return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
        }
        if (diffMonths >= 12) {
            const diffYears = Math.floor(diffMonths / 12);
            return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
        }
        if (diffMonths >= 1) {
            return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
        }
        return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    };

    const fmtRehabLevels = (levels) => {
        if (!levels?.length) return '-';
        return levels.filter(Boolean).map(l => String(l).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ');
    };

    const fmtPropertyTypes = (types) => {
        if (!types?.length) return '-';
        return types.map(t => PROP_TYPE_SHORT[t] || t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ');
    };

    const getGeographySummary = (geography) => {
        if (!geography) return null;
        const parts = [];
        const zipCount = geography.zipCodes?.length || 0;
        const countyCount = geography.counties?.length || 0;
        const polygonCount = geography.polygons?.filter(p => p.active)?.length || 0;
        const stateCount = geography.states?.length || 0;

        if (stateCount > 0) parts.push(`${stateCount} state${stateCount > 1 ? 's' : ''}`);
        if (countyCount > 0) parts.push(`${countyCount} count${countyCount > 1 ? 'ies' : 'y'}`);
        if (zipCount > 0) parts.push(`${zipCount} ZIP${zipCount > 1 ? 's' : ''}`);
        if (polygonCount > 0) parts.push(`${polygonCount} zone${polygonCount > 1 ? 's' : ''}`);

        return parts.length > 0 ? parts.join(', ') : null;
    };

    const formatPropReqs = (reqs) => {
        if (!reqs) return '-';
        const parts = [];
        if (reqs.minBedrooms != null || reqs.maxBedrooms != null) {
            const min = reqs.minBedrooms ?? '?';
            const max = reqs.maxBedrooms ?? '?';
            parts.push(`${min}-${max} beds`);
        }
        if (reqs.minBathrooms != null || reqs.maxBathrooms != null) {
            const min = reqs.minBathrooms ?? '?';
            const max = reqs.maxBathrooms ?? '?';
            parts.push(`${min}-${max} baths`);
        }
        if (reqs.minSquareFeet != null || reqs.maxSquareFeet != null) {
            const min = reqs.minSquareFeet ? numberFormatter.format(reqs.minSquareFeet) : '?';
            const max = reqs.maxSquareFeet ? numberFormatter.format(reqs.maxSquareFeet) : '?';
            parts.push(`${min}-${max} sqft`);
        }
        return parts.length > 0 ? parts.join(', ') : '-';
    };

    const formatDealStructure = (ds) => {
        if (!ds) return '-';
        const parts = [];
        if (ds.acceptsSellerFinancing) parts.push('Seller Finance');
        if (ds.acceptsSubTo) parts.push('Sub-To');
        return parts.length > 0 ? parts.join(', ') : '-';
    };

    // Filter to visible columns
    const visibleColumnsList = visibleColumns
        ? BUYER_COLUMNS.filter(col => visibleColumns.includes(col.key))
        : BUYER_COLUMNS;

    // Apply column ordering
    let displayColumns = columnOrder
        ? columnOrder
            .map(key => visibleColumnsList.find(col => col.key === key))
            .filter(Boolean)
            .concat(visibleColumnsList.filter(col => !columnOrder.includes(col.key)))
        : visibleColumnsList;

    // Build grid template
    const hasActions = onSendDeals || onDeleteBuyer;
    const hasBothActions = onSendDeals && onDeleteBuyer;
    const ACTIONS_COLUMN_WIDTH = hasBothActions ? '144px' : '48px';
    const gridColumns = displayColumns.map(col => col.width).join(' ') + (hasActions ? ` ${ACTIONS_COLUMN_WIDTH}` : '');

    // Render cell content
    const renderCell = (item, column) => {
        const buyerId = item.id || item.buyerId;

        switch (column.key) {
            case 'active': {
                const isActive = item.active !== false;
                return (
                    <div className="buyers-toggle-cell" onClick={(e) => {
                        e.stopPropagation();
                        if (onToggleActive) onToggleActive(buyerId, item.active !== false);
                    }}>
                        <Badge
                            statusDot={true}
                            bgColour={isActive ? 'bg-colour-green-01' : 'bg-colour-gray-01'}
                            text={isActive ? 'Active' : 'Inactive'}
                            textClass="label-bold colour-primary"
                            statusDotColour={isActive ? colour.jade02 : colour.gray04}
                            icon={null}
                            tooltip={false}
                            tooltipBody={null}
                            tooltipPosition="top"
                            iconLeft={false}
                        />
                    </div>
                );
            }

            case 'geography': {
                const summary = getGeographySummary(item.geography);
                if (summary) {
                    return (
                        <span
                            className="buyers-geography-summary body-regular"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onOpenGeoModal) onOpenGeoModal(buyerId, item.geography);
                            }}
                            title="Click to edit geography"
                        >
                            {summary}
                        </span>
                    );
                }
                return (
                    <span
                        className="buyers-geography-add body-regular"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenGeoModal) onOpenGeoModal(buyerId, item.geography);
                        }}
                        title="Click to add geography"
                    >
                        Add area
                    </span>
                );
            }

            case 'rehabLevels':
                return <span className="body-regular" title={fmtRehabLevels(item.rehabLevels)}>{fmtRehabLevels(item.rehabLevels)}</span>;

            case 'propertyTypes':
                return <span className="body-regular" title={fmtPropertyTypes(item.propertyTypes)}>{fmtPropertyTypes(item.propertyTypes)}</span>;

            case 'propReqs':
                return <span className="body-regular">{formatPropReqs(item.propertyRequirements)}</span>;

            case 'pool':
                return <span className="body-regular">{item.propertyRequirements?.requiresPool === true ? 'Required' : '—'}</span>;

            case 'garage': {
                const req = item.propertyRequirements;
                if (req?.requiresGarage !== true) return <span className="body-regular">—</span>;
                const spaces = req?.minGarageSpaces;
                if (spaces != null && !isNaN(Number(spaces)) && Number(spaces) > 0) {
                    return <span className="body-regular">{Number(spaces)}+ car</span>;
                }
                return <span className="body-regular">Required</span>;
            }

            case 'roles': {
                const roles = item.roles;
                if (!roles?.length) return <span className="body-regular">-</span>;
                const formatted = roles.map(r => r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ');
                return <span className="body-regular" title={formatted}>{formatted}</span>;
            }

            case 'dealStructure':
                return <span className="body-regular">{formatDealStructure(item.dealStructure)}</span>;

            case 'createdAt':
                return formatDateTime(item.createdAt);

            case 'updatedAt':
                return formatDateTime(item.updatedAt);

            default:
                break;
        }

        // Handle editable columns
        if (EDITABLE_FIELDS[column.key]) {
            const rawValue = getRawValue(item, column.key);
            return renderEditableCell(item, column.key, null, rawValue);
        }

        return '-';
    };

    // Zero state
    if (!loading && (!data || data.length === 0)) {
        return (
            <div className="buyers-table-container">
                <div className="buyers-empty-state">
                    <p className="body-regular colour-secondary">
                        No buyers yet. Add your first buyer to start matching deals.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="buyers-table-container">
            <div className="buyers-table">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    measuring={{
                        droppable: {
                            strategy: MeasuringStrategy.Always
                        }
                    }}
                    autoScroll={{
                        enabled: true,
                        threshold: { x: 0.1, y: 0 },
                        acceleration: 10
                    }}
                >
                    <SortableContext
                        items={displayColumns.map(c => c.key)}
                        strategy={horizontalListSortingStrategy}
                    >
                        <div className="buyers-table-header" style={{ gridTemplateColumns: gridColumns }}>
                            {displayColumns.map(column => (
                                <DraggableHeader
                                    key={column.key}
                                    column={column}
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    hoveredColumn={hoveredColumn}
                                    onSort={onSort}
                                    onHoverColumn={onHoverColumn}
                                />
                            ))}
                            {hasActions && <div className="buyers-table-header-cell buyers-row-actions-header" />}
                        </div>
                    </SortableContext>
                </DndContext>
                {data.map((item, index) => {
                    const isHovered = hoveredRow === index;
                    return (
                        <div
                            key={item.id || item.buyerId || index}
                            className={`buyers-table-row bg-colour-white ${isHovered ? 'buyers-table-row-hovered' : ''}`}
                            style={{ gridTemplateColumns: gridColumns, cursor: 'pointer' }}
                            onMouseEnter={() => setHoveredRow(index)}
                            onMouseLeave={() => setHoveredRow(null)}
                            onClick={(e) => {
                                // Don't navigate if clicking interactive elements
                                if (e.target.closest('.buyers-edit-container') ||
                                    e.target.closest('.buyers-toggle-cell') ||
                                    e.target.closest('.buyers-row-actions') ||
                                    e.target.closest('.buyers-geography-summary') ||
                                    e.target.closest('.buyers-geography-add') ||
                                    e.target.closest('.buyers-editable-cell-clickable')) {
                                    return;
                                }
                                const id = item.id || item.buyerId;
                                if (id) navigate(`/buyers/${id}`);
                            }}
                        >
                            {displayColumns.map(column => (
                                <div
                                    key={column.key}
                                    className={`buyers-table-cell body-regular colour-primary ${column.key === 'rehabLevels' || column.key === 'propertyTypes' || column.key === 'propReqs' ? 'buyers-table-cell-wrap' : ''}`}
                                >
                                    {renderCell(item, column)}
                                </div>
                            ))}
                            {hasActions && (
                                <div className={`buyers-row-actions ${hasBothActions ? 'buyers-row-actions-both' : 'buyers-row-actions-single'} ${isHovered ? 'bg-colour-x-light' : 'bg-colour-white'}`}>
                                    {onDeleteBuyer && (
                                        <div className="buyers-row-action-slot buyers-row-action-slot-delete">
                                            <div
                                                className="buyers-row-action-btn buyers-row-action-btn-delete"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDeleteBuyer(item.id || item.buyerId, item.contact?.name || 'this buyer');
                                                }}
                                                title="Delete buyer"
                                            >
                                                <span className="buyers-row-action-label label-regular">Delete</span>
                                                <HugeiconsIcon icon={Delete02Icon} color={colour.gray05} size={14} />
                                            </div>
                                        </div>
                                    )}
                                    {onSendDeals && (
                                        <div className="buyers-row-action-slot buyers-row-action-slot-send">
                                            <div
                                                className="buyers-row-action-btn buyers-row-action-btn-send"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSendDeals(item.id || item.buyerId);
                                                }}
                                                title="Send deals"
                                            >
                                                <span className="buyers-row-action-label label-regular">Send Deals</span>
                                                <HugeiconsIcon icon={SentIcon} color={colour.gray05} size={14} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export { BUYER_COLUMNS };
export default BuyersTable;
